/**
 * Bottle clicker — kaucja table, stacked businesses, and save parsing.
 * Physics and canvas stay in the web app; this stays DOM-free so the shop
 * cannot drift from the tests.
 *
 * Rates match the Polish system kaucyjny: PET and cans 50 gr, returnable glass 1 zł.
 * Buildings stack like Cookie Clicker (each copy adds output, cost grows COST_GROWTH^n).
 */
import { BOTTLE_CLICKER_INPUT } from "./bottleClickerInput.js";

export type BottleClickerFamily = "pet" | "can" | "glass";
export type BottleClickerBottle = BottleClickerFamily | "crushed" | "sack";
export type BottleClickerArtId =
  | "pet"
  | "pet2"
  | "pet3"
  | "can"
  | "can2"
  | "can3"
  | "glass"
  | "glass2"
  | "glass3"
  | "crushed"
  | "sack";
export type BottleClickerUpgradeId =
  | "spawn"
  | "grab"
  | "auto"
  | "mix"
  | "click"
  | "firma"
  | "sack";
export type BottleClickerBuyMode = 1 | 10 | "max";

export const BOTTLE_CLICKER = {
  SAVE_VERSION: 8,
  FIELD_WIDTH: 1280,
  FIELD_HEIGHT: 720,
  MACHINE_WIDTH: 280,
  MACHINE_HEIGHT: 420,
  /** Intake hole on butelkomat-idle.png, in sprite pixels. */
  MACHINE_MOUTH_X: 186,
  MACHINE_MOUTH_Y: 86,
  MACHINE_MOUTH_R: 34,
  /** Transparent LCD cutout on the machine face, in sprite pixels. */
  MACHINE_SCREEN_X: 34,
  MACHINE_SCREEN_Y: 56,
  MACHINE_SCREEN_W: 84,
  MACHINE_SCREEN_H: 64,
  MAX_BOTTLES: 56,
  /**
   * Steeper than the Cookie Clicker 1.15: the payout multipliers stack, so cheap
   * copies used to compound a fresh run into 1 mln zł inside two minutes.
   */
  COST_GROWTH: 1.32,
  /** Sklep osiedlowy: 1 bottle a second at level 0, the 15/s ceiling at level 50. */
  BASE_SPAWN_PER_SEC: 1,
  SPAWN_PER_LEVEL: 0.28,
  MAX_SPAWN_PER_SEC: 15,
  /** Zbieracz: half a bottle a second per copy, so the spawn ceiling lands on level 30. */
  AUTO_PER_LEVEL: 0.5,
  /** Collectors cannot outpace the bottles, so this matches MAX_SPAWN_PER_SEC. */
  MAX_AUTO_PER_SEC: 15,
  BASE_CLICK_PAD: 10,
  CLICK_PAD_PER_GRAB: 15,
  CLICK_PAD_PER_CLICK: 10,
  FIRMA_BONUS: 0.12,
  CLICK_BONUS: 0.1,
  /** Combo tops out at ×2.44 — still worth chasing, no longer a fourfold windfall. */
  COMBO_STEP: 0.06,
  COMBO_CAP: 24,
  LUCKY_MULT: 12,
  BASE_LUCKY_CHANCE: 0.012,
  /** Lucky chance reaches its ceiling on Hurtownia 15. */
  MIX_LUCKY_CHANCE: 0.0012,
  MAX_LUCKY_CHANCE: 0.03,
  MIX_GLASS_FROM_LEVEL: 3,
  MIX_GLASS_START_WEIGHT: 150,
  MIX_GLASS_WEIGHT_PER_LEVEL: 80,
  MIX_GLASS_MAX_WEIGHT: 700,
  MIX_CAN_SHARE: 0.45,
  MAX_UPGRADE_LEVEL: 200,
  MAX_GROSZE: 1_000_000_000_000_000,
  OFFLINE_CAP_MS: 8 * 60 * 60 * 1000,
  OFFLINE_MIN_MS: 10 * 60 * 1000,
  CRUSHED_UNLOCK_RETURNED: 10,
  CRUSHED_MAX_ON_FIELD: 1,
  CRUSHED_FIRST_DELAY_SEC: 8,
  CRUSHED_COOLDOWN_MIN_SEC: 22,
  CRUSHED_COOLDOWN_MAX_SEC: 40,
  CRUSHED_HIT_PAD: 6,
  CRUSHED_GRACE_GROSZE: 100,
  CRUSHED_MIN_BOTTLES: 5,
  CRUSHED_WALLET_SHARE: 0.1,
  CRUSHED_WALLET_CAP: 0.25,
  /**
   * Przyjazny menel: bronze/silver/gold/crystal catch streak. Give him a hot-dog
   * and kaucja times that tier for a short window. Miss drops one tier.
   * Cooldown starts on catch or miss. Collectors never catch him.
   * Size/speed/lifetime aliases below are the brązowy menel.
   */
  HUNT_UNLOCK_RETURNED: 20,
  HUNT_FIRST_DELAY_SEC: 8,
  HUNT_COOLDOWN_MIN_SEC: 220,
  HUNT_COOLDOWN_MAX_SEC: 310,
  HUNT_LIFETIME_SEC: 12,
  HUNT_SPEED_MIN: 520,
  HUNT_SPEED_MAX: 680,
  HUNT_SIZE: 80,
  HUNT_HIT_PAD: 14,
  HUNT_MULT: 2,
  HUNT_BUFF_SEC: 60,
  HUNT_STREAK_MAX: 99,
  /**
   * Policja: a slow bouncing cop. Direct click only — splash and zbieracze miss it.
   * Ignore it and it leaves. Tap it and kaucja halves for a few seconds while extra
   * crushed bottles drop. Large and slow so it is obvious, infrequent so it is not a nag.
   */
  POLICE_UNLOCK_RETURNED: 30,
  POLICE_FIRST_DELAY_SEC: 20,
  POLICE_COOLDOWN_MIN_SEC: 150,
  POLICE_COOLDOWN_MAX_SEC: 210,
  POLICE_LIFETIME_SEC: 9,
  POLICE_SPEED_MIN: 160,
  POLICE_SPEED_MAX: 240,
  POLICE_SIZE: 96,
  POLICE_HIT_PAD: 12,
  POLICE_PAYOUT_MULT: 0.5,
  POLICE_CRUSHED_MULT: 3,
  POLICE_CRUSHED_BURST: 2,
  POLICE_BUFF_SEC: 8,
  /**
   * Hulajnogarz: a kid on a scooter, one pass across the floor. Catch him by
   * hand for a basket of PET/cans. Miss and he snitches — policja spawns.
   * Collectors never catch him. Cooldown starts on catch or miss.
   */
  SCOOTER_UNLOCK_RETURNED: 50,
  SCOOTER_FIRST_DELAY_SEC: 22,
  SCOOTER_COOLDOWN_MIN_SEC: 200,
  SCOOTER_COOLDOWN_MAX_SEC: 280,
  SCOOTER_SPEED_MIN: 720,
  SCOOTER_SPEED_MAX: 880,
  /** ~3/4 of the butelkomat sprite (280×420). */
  SCOOTER_WIDTH: 210,
  SCOOTER_HEIGHT: 315,
  SCOOTER_HIT_PAD: 28,
  /** Feet sit on FIELD_HEIGHT minus this, matching the field FLOOR_Y inset. */
  SCOOTER_FLOOR_INSET: 10,
  /** Parking-lot back line, as a fraction down the butelkomat — bottle rest / Skup. */
  LOT_FLOOR_FRAC: 0.81,
  SCOOTER_BASKET_MIN: 8,
  SCOOTER_BASKET_MAX: 12,
  /**
   * Worek z butelkami: a rare falling sack that never rests on the floor.
   * Catch it by hand for a short spawn rain. Shop copies only after 10 restarts
   * and only make the rain denser — not longer, not more often.
   */
  SACK_UNLOCK_RETURNED: 100,
  SACK_UNLOCK_PRESTIGE: 10,
  SACK_FIRST_DELAY_SEC: 40,
  SACK_COOLDOWN_MIN_SEC: 480,
  SACK_COOLDOWN_MAX_SEC: 720,
  SACK_MAX_ON_FIELD: 1,
  SACK_HIT_PAD: 8,
  SACK_BUFF_SEC: 22,
  SACK_MULT_BASE: 2.5,
  SACK_MULT_PER_LEVEL: 0.25,
  SACK_MAX_MULT: 6,
  LEADERBOARD_SIZE: 10,
  /** 1 mln zł earned this run — round formatKaucja breakpoint, a stretch past mid-game. */
  PRESTIGE_MIN_GROSZE: 100_000_000,
  /**
   * Restart bonus scales with √(zł / 1 mln): 1 mln → +25%, 4 mln → +50%, 9 mln → +75%.
   * ASAP at the threshold matches the old flat +25%. Caps at MAX_PRESTIGE × this.
   * 60 restarts → +15 → ×16. First 40 still land on the old ×11.
   */
  PRESTIGE_BONUS: 0.25,
  MAX_PRESTIGE: 60,
  /**
   * Parse/PUT ceiling only so a hostile save cannot OOM. Not a play cap —
   * every wypłata slip stays until the player spends it at the Dealer.
   */
  COUPON_PARSE_MAX: 2000,
  /** Dealer “Witaminy na receptę”: casino chips per coupon, 100 buys/account. */
  DEALER_CHIP_PACK: 10_000,
  DEALER_CHIP_PACKS_MAX: 100,
  DEALER_CHIP_COUPON_COST: 1,
  /** Paczka od znajomego: 1 coupon, repeatable. Never żetony. */
  DEALER_MYSTERY_COUPON_COST: 1,
  DEALER_MYSTERY_SKUP_CHANCE: 0.15,
  DEALER_MYSTERY_ZLOTE_CHANCE: 0.4,
  DEALER_MYSTERY_ZLOTE_MIN: 500,
  DEALER_MYSTERY_ZLOTE_SPAN: 2001,
  DEALER_SKUP_TOKEN_MS: 2 * 60 * 60 * 1000,
  /** Przedłużenie parkingu: 3× +4 h on the 8 h Skup cap (12 / 16 / 20 h). */
  DEALER_OFFLINE_STEP_MS: 4 * 60 * 60 * 1000,
  DEALER_OFFLINE_STEPS_MAX: 3,
  DEALER_OFFLINE_BONUS_MAX_MS: 12 * 60 * 60 * 1000,
  DEALER_OFFLINE_COUPON_COSTS: [2, 3, 5],
  /** Karnet dla kolegi: 3× +1 menel baseline. Magazyn still has no menel. */
  DEALER_MENEL_FLOOR_MAX: 3,
  DEALER_MENEL_COUPON_COSTS: [2, 4, 8],
  DEALER_WIDTH: 110,
  DEALER_HEIGHT: 160,
  DEALER_HIT_PAD: 14,
} as const;

export type DealerCatalogId = "chips" | "mystery" | "parking" | "menel";
export type DealerMysteryKind = "joke" | "zlote" | "skup";

export interface DealerMysteryRoll {
  kind: DealerMysteryKind;
  grosze: number;
  joke: string;
}

export const DEALER_MYSTERY_JOKES: readonly string[] = [
  "W środku była zgnieciona rolka z kasy. Sucha.",
  "Kartka: „oddaj bon”. Kaucji brak.",
  "Pusty worek. Pachnie tanim piwem.",
  "Gumowy korek i życzenia zdrowia.",
  "Zdrapka już zdrapana. Wygrałeś uśmiech.",
  "Butelka bez kaucji. Klasyka gatunku.",
  "Instrukcja składania wora. Wór nie dołączony.",
  "Guzik od bluzy i pozdrowienia od teściowej.",
];

export type HuntTierId = 1 | 2 | 3 | 4;
export type HuntTierKey = "bronze" | "silver" | "gold" | "crystal";

export interface HuntTierDef {
  id: HuntTierId;
  key: HuntTierKey;
  label: string;
  /** Accusative for catch toasts: "Zrekrutowałeś {caughtName}". */
  caughtName: string;
  minStreak: number;
  mult: number;
  buffSec: number;
  size: number;
  hitPad: number;
  lifetimeSec: number;
  speedMin: number;
  speedMax: number;
}

/** Highest unlocked menel for a catch streak. Thresholds 0 / 3 / 8 / 15. */
export const HUNT_TIERS: readonly HuntTierDef[] = [
  {
    id: 1,
    key: "bronze",
    label: "Brązowy menel",
    caughtName: "brązowego menela",
    minStreak: 0,
    mult: BOTTLE_CLICKER.HUNT_MULT,
    buffSec: BOTTLE_CLICKER.HUNT_BUFF_SEC,
    size: BOTTLE_CLICKER.HUNT_SIZE,
    hitPad: BOTTLE_CLICKER.HUNT_HIT_PAD,
    lifetimeSec: BOTTLE_CLICKER.HUNT_LIFETIME_SEC,
    speedMin: BOTTLE_CLICKER.HUNT_SPEED_MIN,
    speedMax: BOTTLE_CLICKER.HUNT_SPEED_MAX,
  },
  {
    id: 2,
    key: "silver",
    label: "Srebrny menel",
    caughtName: "srebrnego menela",
    minStreak: 3,
    mult: 2.5,
    buffSec: BOTTLE_CLICKER.HUNT_BUFF_SEC,
    size: 68,
    hitPad: 10,
    lifetimeSec: 10,
    speedMin: 640,
    speedMax: 820,
  },
  {
    id: 3,
    key: "gold",
    label: "Złoty menel",
    caughtName: "złotego menela",
    minStreak: 8,
    mult: 3,
    buffSec: BOTTLE_CLICKER.HUNT_BUFF_SEC,
    size: 56,
    hitPad: 7,
    lifetimeSec: 8,
    speedMin: 780,
    speedMax: 980,
  },
  {
    id: 4,
    key: "crystal",
    label: "Kryształowy menel",
    caughtName: "kryształowego menela",
    minStreak: 15,
    mult: 4,
    buffSec: BOTTLE_CLICKER.HUNT_BUFF_SEC,
    size: 46,
    hitPad: 4,
    lifetimeSec: 6.5,
    speedMin: 940,
    speedMax: 1180,
  },
];

export const BOTTLE_CLICKER_KAUCJA: Record<
  Exclude<BottleClickerBottle, "crushed" | "sack">,
  number
> = {
  pet: 50,
  can: 50,
  glass: 100,
};

export const BOTTLE_CLICKER_SPRITE: Record<BottleClickerBottle, { width: number; height: number }> =
  {
    pet: { width: 80, height: 160 },
    can: { width: 80, height: 112 },
    glass: { width: 80, height: 180 },
    crushed: { width: 80, height: 100 },
    sack: { width: 120, height: 140 },
  };

/** Three skins per deposit family. Same size and kaucja as the original. */
export const BOTTLE_CLICKER_VARIANTS: Record<BottleClickerFamily, readonly BottleClickerArtId[]> = {
  pet: ["pet", "pet2", "pet3"],
  can: ["can", "can2", "can3"],
  glass: ["glass", "glass2", "glass3"],
};

/**
 * Levels needed for `base + n * perLevel` to reach `cap`. The epsilon keeps float
 * dust (0.03 - 0.012 lands a hair above 12 steps) from adding a dead level.
 */
function levelsToReach(base: number, perLevel: number, cap: number): number {
  return Math.max(0, Math.ceil((cap - base) / perLevel - 1e-9));
}

/** Copies of Chwyt after which a wider splash cannot reach anything new. */
const GRAB_CAP_LEVELS = 100;

/**
 * A splash this wide reaches every bottle on the field from any tap: a hundred
 * copies of Chwyt clear the 1469 px field diagonal with room to spare.
 */
export const MAX_SPLASH_RADIUS_PX =
  BOTTLE_CLICKER.BASE_CLICK_PAD + GRAB_CAP_LEVELS * BOTTLE_CLICKER.CLICK_PAD_PER_GRAB;

/**
 * Where each business stops changing anything, so the shop can lock it there.
 * Sklep, zbieracz, chwyt and hurtownia all run into their own ceiling; moc
 * kliknięcia and operator are flat multipliers that never stop paying, so they
 * only meet the absolute ceiling — cost growth walls them off long before that.
 *
 * The rates above are picked so every ceiling lands on a round level:
 * sklep 50, chwyt 100, zbieracz 30, hurtownia 15, the multipliers 200.
 */
export const BOTTLE_CLICKER_MAX_LEVEL: Record<BottleClickerUpgradeId, number> = {
  spawn: levelsToReach(
    BOTTLE_CLICKER.BASE_SPAWN_PER_SEC,
    BOTTLE_CLICKER.SPAWN_PER_LEVEL,
    BOTTLE_CLICKER.MAX_SPAWN_PER_SEC,
  ),
  grab: levelsToReach(BOTTLE_CLICKER.BASE_CLICK_PAD, BOTTLE_CLICKER.CLICK_PAD_PER_GRAB, MAX_SPLASH_RADIUS_PX),
  auto: levelsToReach(0, BOTTLE_CLICKER.AUTO_PER_LEVEL, BOTTLE_CLICKER.MAX_AUTO_PER_SEC),
  // Hurtownia does two jobs: the bottle table and the lucky roll. Whichever
  // ceiling comes last is the level where the copy stops being worth anything.
  mix: Math.max(
    levelsToReach(
      BOTTLE_CLICKER.BASE_LUCKY_CHANCE,
      BOTTLE_CLICKER.MIX_LUCKY_CHANCE,
      BOTTLE_CLICKER.MAX_LUCKY_CHANCE,
    ),
    BOTTLE_CLICKER.MIX_GLASS_FROM_LEVEL +
      levelsToReach(
        BOTTLE_CLICKER.MIX_GLASS_START_WEIGHT,
        BOTTLE_CLICKER.MIX_GLASS_WEIGHT_PER_LEVEL,
        BOTTLE_CLICKER.MIX_GLASS_MAX_WEIGHT,
      ),
  ),
  click: BOTTLE_CLICKER.MAX_UPGRADE_LEVEL,
  firma: BOTTLE_CLICKER.MAX_UPGRADE_LEVEL,
  sack: levelsToReach(
    BOTTLE_CLICKER.SACK_MULT_BASE,
    BOTTLE_CLICKER.SACK_MULT_PER_LEVEL,
    BOTTLE_CLICKER.SACK_MAX_MULT,
  ),
};

export function upgradeMaxLevel(id: BottleClickerUpgradeId): number {
  return BOTTLE_CLICKER_MAX_LEVEL[id];
}

export interface BottleClickerUpgradeDef {
  id: BottleClickerUpgradeId;
  label: string;
  icon: string;
  flavor: string;
  baseCost: number;
  /** Grosze earned this run before this business appears as buyable. */
  unlockLifetime: number;
  /** Restarts already banked before this business appears as buyable. */
  unlockPrestige?: number;
  /** Skip the locked teaser — the row only appears once it can be bought. */
  hideUntilUnlocked?: boolean;
}

export const BOTTLE_CLICKER_UPGRADES: readonly BottleClickerUpgradeDef[] = [
  {
    id: "spawn",
    label: "Żabka",
    icon: "🏪",
    flavor: "Więcej Żabek, więcej butelek.",
    baseCost: 50,
    unlockLifetime: 0,
  },
  {
    id: "grab",
    label: "Torba",
    icon: "✋",
    flavor: "Większa torba, większy zasięg.",
    baseCost: 75,
    unlockLifetime: 0,
  },
  {
    id: "auto",
    label: "Zbieracze",
    icon: "🧺",
    flavor: "Gówniarze z osiedla zbierają.",
    baseCost: 100,
    unlockLifetime: 50,
  },
  {
    id: "mix",
    label: "Magazyn",
    icon: "🍾",
    flavor: "Większy asortyment, lepsze butelki.",
    baseCost: 800,
    unlockLifetime: 1_000,
  },
  {
    id: "click",
    label: "Siłka",
    icon: "💥",
    flavor: "Większa łapa, większy rozmach.",
    baseCost: 15_000,
    unlockLifetime: 18_000,
  },
  {
    id: "firma",
    label: "Lobbing",
    icon: "🏢",
    flavor: "Przekup polityków, więcej kaucji.",
    baseCost: 80_000,
    unlockLifetime: 100_000,
  },
  {
    id: "sack",
    label: "Worek z butelkami",
    icon: "🛍️",
    flavor: "Złap worek — deszcz butelek.",
    baseCost: 250_000,
    unlockLifetime: 100_000,
    unlockPrestige: BOTTLE_CLICKER.SACK_UNLOCK_PRESTIGE,
    hideUntilUnlocked: true,
  },
];

export type BottleClickerUpgrades = Record<BottleClickerUpgradeId, number>;

/** One printed wypłata slip. Stored on the save so history syncs with the cloud. */
export interface BottleClickerCoupon {
  runGrosze: number;
  returnedBottles: number;
  lines: readonly { label: string; count: number; grosze: number }[];
  nextMult: number;
  bonusPct: number;
  chipsGranted: number;
  prestigeCountAfter: number;
  printedAtMs: number;
  barcode: string;
  txnId: string;
}

export interface BottleClickerSave {
  version: number;
  grosze: number;
  lifetimeGrosze: number;
  /** Kaucja earned since the last restart. Unlocks and prestige gate on this, not lifetime. */
  runLifetimeGrosze: number;
  returnedBottles: number;
  bestCombo: number;
  prestigeCount: number;
  /**
   * Permanent additive kaucja bonus from restarts (√zł each time).
   * Multiplier is `1 + prestigeBonus`, capped at MAX_PRESTIGE × PRESTIGE_BONUS.
   */
  prestigeBonus: number;
  /** Wypłata coupons, newest first. Spend at the Dealer; no play cap. */
  coupons: BottleClickerCoupon[];
  /** Dealer karnet: menel spawn floor 0=brąz … 3=kryształ. */
  dealerMenelFloor: number;
  /** Extra offline cap ms from Dealer parking hours (4 h each, max 12 h). */
  dealerOfflineBonusMs: number;
  /** 0 default machine, 1 alt skin (Folia na szafie). */
  dealerMachineSkin: number;
  /** One-Skup extra hours token from Paczka od znajomego. */
  dealerSkupBonusMs: number;
  /**
   * Bumped only by an admin wipe. A higher generation always wins a merge, so
   * a zeroed cloud save cannot be overwritten by an old localStorage run.
   */
  wipeGeneration: number;
  lastSeenMs: number;
  upgrades: BottleClickerUpgrades;
  /** Consecutive menel catches. Next spawn uses huntTierForStreak. Miss demotes one tier. */
  huntStreak: number;
  /** Account that last wrote this browser save. Guest/local is null. */
  ownerUserId: number | null;
  huntEncounter: HuntEncounter | null;
  huntBuffUntilMs: number;
  huntBuffTier: HuntTierId | 0;
  /** Cooldown left when last saved. 0 = first-delay on mount. */
  nextHuntRemainingMs: number;
  policeEncounter: BounceEncounter | null;
  policeBuffUntilMs: number;
  nextPoliceRemainingMs: number;
  sackBuffUntilMs: number;
  nextSackRemainingMs: number;
  scooterEncounter: BounceEncounter | null;
  nextScooterRemainingMs: number;
}

/** Public ranking row. Sorted by lifetime kaucja, then bottles returned. */
export interface BottleClickerLeaderRow {
  userId: number;
  username: string;
  lifetimeGrosze: number;
  returnedBottles: number;
  grosze: number;
  bestCombo: number;
  updatedAt: string;
}

export interface BottleWeights {
  pet: number;
  can: number;
  glass: number;
}

export interface BottleReturnContext {
  combo: number;
  manual: boolean;
  lucky: boolean;
  hunt?: boolean;
  /** Active menel payout multiplier while hunt is true. Defaults to bronze ×2. */
  huntMult?: number;
  police?: boolean;
}

export interface DvdBounceState {
  x: number;
  y: number;
  vx: number;
  vy: number;
  bornMs: number;
}

export interface HuntState extends DvdBounceState {
  tier: HuntTierId;
}

export type PoliceState = DvdBounceState;

export type ScooterState = DvdBounceState;

/** Pose + remaining lifetime. remainingMs is frozen while Skup is up. */
export interface BounceEncounter {
  remainingMs: number;
  savedAtMs: number;
  paused?: boolean;
  x: number;
  y: number;
  vx: number;
  vy: number;
}

export interface HuntEncounter extends BounceEncounter {
  tier: HuntTierId;
}

export interface SpawnedBottle {
  type: BottleClickerBottle;
  art: BottleClickerArtId;
  lucky: boolean;
}

const UPGRADE_IDS: readonly BottleClickerUpgradeId[] = [
  "spawn",
  "grab",
  "auto",
  "mix",
  "click",
  "firma",
  "sack",
];

const EMPTY_UPGRADES: BottleClickerUpgrades = {
  spawn: 0,
  grab: 0,
  auto: 0,
  mix: 0,
  click: 0,
  firma: 0,
  sack: 0,
};

const DEFAULT_RETURN: BottleReturnContext = {
  combo: 1,
  manual: true,
  lucky: false,
  hunt: false,
  police: false,
};

export function emptyBottleClickerSave(): BottleClickerSave {
  return {
    version: BOTTLE_CLICKER.SAVE_VERSION,
    grosze: 0,
    lifetimeGrosze: 0,
    runLifetimeGrosze: 0,
    returnedBottles: 0,
    bestCombo: 0,
    prestigeCount: 0,
    prestigeBonus: 0,
    coupons: [],
    wipeGeneration: 0,
    lastSeenMs: 0,
    upgrades: { ...EMPTY_UPGRADES },
    huntStreak: 0,
    ...emptyHuntSessionFields(),
    ...emptyDealerFields(),
    ownerUserId: null,
  };
}

function emptyHuntSessionFields(): Pick<
  BottleClickerSave,
  | "huntEncounter"
  | "huntBuffUntilMs"
  | "huntBuffTier"
  | "nextHuntRemainingMs"
  | "policeEncounter"
  | "policeBuffUntilMs"
  | "nextPoliceRemainingMs"
  | "sackBuffUntilMs"
  | "nextSackRemainingMs"
  | "scooterEncounter"
  | "nextScooterRemainingMs"
> {
  return {
    huntEncounter: null,
    huntBuffUntilMs: 0,
    huntBuffTier: 0,
    nextHuntRemainingMs: 0,
    policeEncounter: null,
    policeBuffUntilMs: 0,
    nextPoliceRemainingMs: 0,
    sackBuffUntilMs: 0,
    nextSackRemainingMs: 0,
    scooterEncounter: null,
    nextScooterRemainingMs: 0,
  };
}

function emptyDealerFields(): Pick<
  BottleClickerSave,
  "dealerMenelFloor" | "dealerOfflineBonusMs" | "dealerMachineSkin" | "dealerSkupBonusMs"
> {
  return {
    dealerMenelFloor: 0,
    dealerOfflineBonusMs: 0,
    dealerMachineSkin: 0,
    dealerSkupBonusMs: 0,
  };
}

function copyDealerFields(
  save: BottleClickerSave,
): Pick<
  BottleClickerSave,
  "dealerMenelFloor" | "dealerOfflineBonusMs" | "dealerMachineSkin" | "dealerSkupBonusMs"
> {
  return {
    dealerMenelFloor: clampInt(save.dealerMenelFloor, 0, BOTTLE_CLICKER.DEALER_MENEL_FLOOR_MAX),
    dealerOfflineBonusMs: clampInt(
      save.dealerOfflineBonusMs,
      0,
      BOTTLE_CLICKER.DEALER_OFFLINE_BONUS_MAX_MS,
    ),
    dealerMachineSkin: clampInt(save.dealerMachineSkin, 0, 1),
    dealerSkupBonusMs: clampInt(save.dealerSkupBonusMs, 0, BOTTLE_CLICKER.DEALER_SKUP_TOKEN_MS),
  };
}

export function bottleClickerUpgrade(id: BottleClickerUpgradeId): BottleClickerUpgradeDef {
  const found = BOTTLE_CLICKER_UPGRADES.find((item) => item.id === id);
  if (!found) throw new Error(`unknown upgrade ${id}`);
  return found;
}

export function upgradeCost(id: BottleClickerUpgradeId, level: number): number {
  const safe = clampInt(level, 0, upgradeMaxLevel(id));
  const base = bottleClickerUpgrade(id).baseCost;
  return Math.floor(base * BOTTLE_CLICKER.COST_GROWTH ** safe + 1e-9);
}

/** Sum of the next `count` stacked copies (Cookie Clicker buy-10 / buy-max). */
export function upgradeCostForCount(
  id: BottleClickerUpgradeId,
  level: number,
  count: number,
): number {
  const max = upgradeMaxLevel(id);
  const n = clampInt(count, 0, max);
  const start = clampInt(level, 0, max);
  let total = 0;
  for (let i = 0; i < n; i += 1) {
    if (start + i >= max) break;
    total += upgradeCost(id, start + i);
    if (total >= BOTTLE_CLICKER.MAX_GROSZE) return BOTTLE_CLICKER.MAX_GROSZE;
  }
  return total;
}

export function maxAffordableCount(
  save: BottleClickerSave,
  id: BottleClickerUpgradeId,
): number {
  if (!isUpgradeUnlocked(save, id)) return 0;
  let bought = 0;
  let spent = 0;
  const start = save.upgrades[id];
  while (start + bought < upgradeMaxLevel(id)) {
    const cost = upgradeCost(id, start + bought);
    if (spent + cost > save.grosze) break;
    spent += cost;
    bought += 1;
  }
  return bought;
}

export function plannedBuyCount(
  save: BottleClickerSave,
  id: BottleClickerUpgradeId,
  mode: BottleClickerBuyMode,
): number {
  const affordable = maxAffordableCount(save, id);
  if (mode === "max") return affordable;
  // Near the cap ×10 buys whatever is left rather than greying out for good.
  const room = Math.max(0, upgradeMaxLevel(id) - save.upgrades[id]);
  const want = Math.min(mode, room);
  return want > 0 && affordable >= want ? want : 0;
}

export function isUpgradeUnlocked(save: BottleClickerSave, id: BottleClickerUpgradeId): boolean {
  const upgrade = bottleClickerUpgrade(id);
  if (save.runLifetimeGrosze < upgrade.unlockLifetime) return false;
  return save.prestigeCount >= (upgrade.unlockPrestige ?? 0);
}

export function isUpgradeMaxed(save: BottleClickerSave, id: BottleClickerUpgradeId): boolean {
  return save.upgrades[id] >= upgradeMaxLevel(id);
}

/** Max permanent additive bonus — same ceiling as 60 × flat +25%. */
export function maxPrestigeBonus(): number {
  return BOTTLE_CLICKER.MAX_PRESTIGE * BOTTLE_CLICKER.PRESTIGE_BONUS;
}

/**
 * Bonus granted by one restart at the given run total.
 * √(zł / 1 mln) × 25%: threshold match is still +25%, longer runs pay more slowly.
 */
export function prestigeRunBonus(runLifetimeGrosze: number): number {
  const ratio = Math.max(0, runLifetimeGrosze) / BOTTLE_CLICKER.PRESTIGE_MIN_GROSZE;
  return BOTTLE_CLICKER.PRESTIGE_BONUS * Math.sqrt(ratio);
}

/** Permanent kaucja multiplier from the accumulated restart bonus. */
export function prestigeMultiplier(prestigeBonus: number): number {
  return 1 + clampPrestigeBonus(prestigeBonus);
}

export function canPrestige(save: BottleClickerSave): boolean {
  return save.runLifetimeGrosze >= BOTTLE_CLICKER.PRESTIGE_MIN_GROSZE;
}

export function prestigeRemainingGrosze(save: BottleClickerSave): number {
  return Math.max(0, BOTTLE_CLICKER.PRESTIGE_MIN_GROSZE - save.runLifetimeGrosze);
}

/**
 * Wipe the shop and wallet, keep ranking totals, combo record, coupon
 * history and Dealer unlocks. Chip grants happen at the Dealer, not here —
 * pass `chipsGranted` only if a printed slip should mention a grant.
 */
export function prestigeSave(
  save: BottleClickerSave,
  now = Date.now(),
  opts: { chipsGranted?: number } = {},
): BottleClickerSave | null {
  if (!canPrestige(save)) return null;
  const chipsGranted = Math.max(0, Math.floor(opts.chipsGranted ?? 0));
  const nextBonus = clampPrestigeBonus(save.prestigeBonus + prestigeRunBonus(save.runLifetimeGrosze));
  const coupon = buildBottleClickerCoupon(save, {
    chipsGranted,
    nextMult: prestigeMultiplier(nextBonus),
    nowMs: now,
  });
  return {
    version: BOTTLE_CLICKER.SAVE_VERSION,
    grosze: 0,
    lifetimeGrosze: save.lifetimeGrosze,
    runLifetimeGrosze: 0,
    returnedBottles: 0,
    bestCombo: save.bestCombo,
    prestigeCount: clampInt(save.prestigeCount + 1, 0, 999),
    prestigeBonus: nextBonus,
    coupons: [coupon, ...save.coupons],
    wipeGeneration: save.wipeGeneration,
    lastSeenMs: now,
    upgrades: { ...EMPTY_UPGRADES },
    huntStreak: dealerMenelFloorTier(save.dealerMenelFloor).minStreak,
    ownerUserId: save.ownerUserId,
    ...emptyHuntSessionFields(),
    ...copyDealerFields(save),
  };
}

/** Drop the oldest `n` wypłata slips (newest stay at the front). */
export function spendCoupons(save: BottleClickerSave, n: number): BottleClickerSave | null {
  const count = Math.max(0, Math.floor(n));
  if (count <= 0) return save;
  if (save.coupons.length < count) return null;
  return { ...save, coupons: save.coupons.slice(0, save.coupons.length - count) };
}

export type DealerChipPackBlock = "no-coupon" | "sold-out";

/** Shared gate for the Dealer chip pack. Pack count lives on the casino wallet. */
export function dealerChipPackVerdict(
  save: BottleClickerSave,
  packsBought: number,
): { ok: true } | { ok: false; reason: DealerChipPackBlock } {
  const packs = clampInt(packsBought, 0, Number.MAX_SAFE_INTEGER);
  if (packs >= BOTTLE_CLICKER.DEALER_CHIP_PACKS_MAX) return { ok: false, reason: "sold-out" };
  if (save.coupons.length < BOTTLE_CLICKER.DEALER_CHIP_COUPON_COST) {
    return { ok: false, reason: "no-coupon" };
  }
  return { ok: true };
}

export function dealerChipPacksLeft(packsBought: number): number {
  const packs = clampInt(packsBought, 0, BOTTLE_CLICKER.DEALER_CHIP_PACKS_MAX);
  return BOTTLE_CLICKER.DEALER_CHIP_PACKS_MAX - packs;
}

/** Shape of the previous live page’s prestige chip-rank line. */
export type BottleClickerChipTier = {
  label: string;
  chips: number;
  fromPrestige: number;
};

const LEGACY_CHIP_TIER: BottleClickerChipTier = { label: "—", chips: 0, fromPrestige: 0 };

/** Previous live page. Chip ranks moved to the Dealer pack; keep until that UI ships. */
export function chipTierFor(_prestigeCount: number): BottleClickerChipTier {
  return LEGACY_CHIP_TIER;
}

export function nextChipTier(_prestigeCount: number): BottleClickerChipTier | null {
  return null;
}

export function prestigeChipReward(_saveOrCount: unknown): number {
  return 0;
}

/** NPC + stall after the first printed coupon (or any restart count). */
export function dealerUnlocked(save: BottleClickerSave): boolean {
  return save.coupons.length > 0 || save.prestigeCount > 0;
}

export function formatBonCost(n: number): string {
  const count = Math.max(0, Math.floor(n));
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (count === 1) return "1 bon";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${count} bony`;
  return `${count} bonów`;
}

/** @deprecated Use formatBonCost. */
export function formatDealerCouponCost(n: number): string {
  return formatBonCost(n);
}

export function dealerParkingStepsBought(save: BottleClickerSave): number {
  return clampInt(
    Math.round(save.dealerOfflineBonusMs / BOTTLE_CLICKER.DEALER_OFFLINE_STEP_MS),
    0,
    BOTTLE_CLICKER.DEALER_OFFLINE_STEPS_MAX,
  );
}

export function dealerParkingCouponCost(save: BottleClickerSave): number | null {
  const steps = dealerParkingStepsBought(save);
  if (steps >= BOTTLE_CLICKER.DEALER_OFFLINE_STEPS_MAX) return null;
  return BOTTLE_CLICKER.DEALER_OFFLINE_COUPON_COSTS[steps] ?? null;
}

export function buyDealerParking(save: BottleClickerSave): BottleClickerSave | null {
  const cost = dealerParkingCouponCost(save);
  if (cost == null) return null;
  const spent = spendCoupons(save, cost);
  if (!spent) return null;
  const nextSteps = dealerParkingStepsBought(save) + 1;
  return {
    ...spent,
    dealerOfflineBonusMs: nextSteps * BOTTLE_CLICKER.DEALER_OFFLINE_STEP_MS,
  };
}

export function dealerMenelCouponCost(save: BottleClickerSave): number | null {
  const floor = clampInt(save.dealerMenelFloor, 0, BOTTLE_CLICKER.DEALER_MENEL_FLOOR_MAX);
  if (floor >= BOTTLE_CLICKER.DEALER_MENEL_FLOOR_MAX) return null;
  return BOTTLE_CLICKER.DEALER_MENEL_COUPON_COSTS[floor] ?? null;
}

export function buyDealerMenel(save: BottleClickerSave): BottleClickerSave | null {
  const cost = dealerMenelCouponCost(save);
  if (cost == null) return null;
  const spent = spendCoupons(save, cost);
  if (!spent) return null;
  const floor = clampInt(save.dealerMenelFloor, 0, BOTTLE_CLICKER.DEALER_MENEL_FLOOR_MAX) + 1;
  const minStreak = dealerMenelFloorTier(floor).minStreak;
  return {
    ...spent,
    dealerMenelFloor: floor,
    huntStreak: Math.max(clampHuntStreak(spent.huntStreak), minStreak),
  };
}

export function rollDealerMystery(
  rng: () => number = Math.random,
  alreadyHasSkupToken = false,
): DealerMysteryRoll {
  const kindRoll = clampUnit(rng());
  const skupChance = BOTTLE_CLICKER.DEALER_MYSTERY_SKUP_CHANCE;
  const zloteUntil = skupChance + BOTTLE_CLICKER.DEALER_MYSTERY_ZLOTE_CHANCE;
  if (kindRoll < skupChance && !alreadyHasSkupToken) {
    return { kind: "skup", grosze: 0, joke: "" };
  }
  if (kindRoll < zloteUntil) {
    const span = BOTTLE_CLICKER.DEALER_MYSTERY_ZLOTE_SPAN;
    const grosze = BOTTLE_CLICKER.DEALER_MYSTERY_ZLOTE_MIN + Math.floor(clampUnit(rng()) * span);
    return { kind: "zlote", grosze, joke: "" };
  }
  const jokes = DEALER_MYSTERY_JOKES;
  const index = Math.min(jokes.length - 1, Math.floor(clampUnit(rng()) * jokes.length));
  return { kind: "joke", grosze: 0, joke: jokes[index] ?? "Pusty worek. Pachnie tanim piwem." };
}

function creditRunGrosze(save: BottleClickerSave, grosze: number): BottleClickerSave {
  const earned = Math.max(0, Math.floor(grosze));
  if (earned <= 0) return save;
  return {
    ...save,
    grosze: clampGrosze(save.grosze + earned),
    lifetimeGrosze: clampGrosze(save.lifetimeGrosze + earned),
    runLifetimeGrosze: clampGrosze(save.runLifetimeGrosze + earned),
  };
}

export function buyDealerMystery(
  save: BottleClickerSave,
  rng: () => number = Math.random,
): { save: BottleClickerSave; roll: DealerMysteryRoll } | null {
  if (save.coupons.length < BOTTLE_CLICKER.DEALER_MYSTERY_COUPON_COST) return null;
  const roll = rollDealerMystery(rng, save.dealerSkupBonusMs > 0);
  const spent = spendCoupons(save, BOTTLE_CLICKER.DEALER_MYSTERY_COUPON_COST);
  if (!spent) return null;
  if (roll.kind === "zlote") {
    return { save: creditRunGrosze(spent, roll.grosze), roll };
  }
  if (roll.kind === "skup") {
    return { save: { ...spent, dealerSkupBonusMs: BOTTLE_CLICKER.DEALER_SKUP_TOKEN_MS }, roll };
  }
  return { save: spent, roll };
}

export function consumeDealerSkupToken(save: BottleClickerSave): BottleClickerSave {
  if (save.dealerSkupBonusMs <= 0) return save;
  return { ...save, dealerSkupBonusMs: 0 };
}

/** 8 h base + Dealer parking hours (max 20 h). */
export function offlineCapMs(save: BottleClickerSave): number {
  return (
    BOTTLE_CLICKER.OFFLINE_CAP_MS +
    clampInt(save.dealerOfflineBonusMs, 0, BOTTLE_CLICKER.DEALER_OFFLINE_BONUS_MAX_MS)
  );
}

export function offlineCapHours(save: BottleClickerSave): number {
  return Math.round(offlineCapMs(save) / (60 * 60 * 1000));
}

/**
 * Snapshot a run into a butelkomat-style slip. Mix weights split the bottle
 * count; line values are scaled so they add up to the run's kaucja.
 */
export function buildBottleClickerCoupon(
  save: BottleClickerSave,
  opts: { chipsGranted: number; nextMult: number; nowMs?: number },
): BottleClickerCoupon {
  const nowMs = opts.nowMs ?? Date.now();
  const weights = bottleWeights(save.upgrades.mix);
  const totalW = Math.max(1, weights.pet + weights.can + weights.glass);
  const n = Math.max(0, save.returnedBottles);

  let pet = Math.floor((n * weights.pet) / totalW);
  let can = Math.floor((n * weights.can) / totalW);
  let glass = Math.max(0, n - pet - can);
  if (weights.glass <= 0 && glass > 0) {
    pet += glass;
    glass = 0;
  }

  const raw = [
    { label: "Butelka PET", count: pet, unit: BOTTLE_CLICKER_KAUCJA.pet },
    { label: "Puszka ALU", count: can, unit: BOTTLE_CLICKER_KAUCJA.can },
    { label: "Butelka SZKŁO", count: glass, unit: BOTTLE_CLICKER_KAUCJA.glass },
  ].filter((row) => row.count > 0);

  const baseSum = raw.reduce((sum, row) => sum + row.count * row.unit, 0);
  const scale = baseSum > 0 ? save.runLifetimeGrosze / baseSum : 1;
  let lines = raw.map((row) => ({
    label: row.label,
    count: row.count,
    grosze: Math.round(row.count * row.unit * scale),
  }));

  const lineSum = lines.reduce((sum, row) => sum + row.grosze, 0);
  const drift = save.runLifetimeGrosze - lineSum;
  if (lines.length > 0 && drift !== 0) {
    const last = lines[lines.length - 1]!;
    lines = [...lines.slice(0, -1), { ...last, grosze: Math.max(0, last.grosze + drift) }];
  }

  if (lines.length === 0 && save.runLifetimeGrosze > 0) {
    lines = [{ label: "Kaucja zbiorcza", count: Math.max(1, n), grosze: save.runLifetimeGrosze }];
  }

  const seed = `${save.prestigeCount}${save.runLifetimeGrosze}${save.returnedBottles}${nowMs}`;
  const digits = seed.replace(/\D/g, "").padEnd(28, "9803751578278413029904600743").slice(0, 28);

  return {
    runGrosze: save.runLifetimeGrosze,
    returnedBottles: n,
    lines,
    nextMult: opts.nextMult,
    bonusPct: Math.round(prestigeRunBonus(save.runLifetimeGrosze) * 100),
    chipsGranted: Math.max(0, Math.floor(opts.chipsGranted)),
    prestigeCountAfter: save.prestigeCount + 1,
    printedAtMs: nowMs,
    barcode: digits,
    txnId: `8${String(800_000 + (save.prestigeCount % 1000)).slice(-6)}-${String(90_000_000 + (save.returnedBottles % 10_000_000)).slice(-8)}-${String(10_000 + (save.runLifetimeGrosze % 10_000)).slice(-5)}-00`,
  };
}

/** Fresh save, except wipeGeneration ticks up so old local copies lose the merge. */
export function wipeBottleClickerSave(save: BottleClickerSave, now = Date.now()): BottleClickerSave {
  return {
    ...emptyBottleClickerSave(),
    wipeGeneration: clampInt(save.wipeGeneration + 1, 1, 9999),
    lastSeenMs: now,
  };
}

export interface BottleClickerAdminPatch {
  grosze?: number;
  lifetimeGrosze?: number;
  runLifetimeGrosze?: number;
  returnedBottles?: number;
  bestCombo?: number;
  prestigeCount?: number;
  prestigeBonus?: number;
  upgrades?: Partial<BottleClickerUpgrades>;
}

/**
 * Admin edit of a cloud run. Clamps like a normal parse, then bumps wipeGeneration
 * so an open tab cannot PUT the old local copy back over the change.
 * Count-only edits sync the flat ASAP bonus so older admin tools stay coherent.
 * Wallet gifts also count toward this run (and lifetime) so the restart menu moves.
 */
export function applyAdminBottleClickerPatch(
  save: BottleClickerSave,
  patch: BottleClickerAdminPatch,
  now = Date.now(),
): BottleClickerSave {
  const prestigeCount = patch.prestigeCount ?? save.prestigeCount;
  const prestigeBonus =
    patch.prestigeBonus !== undefined
      ? patch.prestigeBonus
      : patch.prestigeCount !== undefined
        ? prestigeCount * BOTTLE_CLICKER.PRESTIGE_BONUS
        : save.prestigeBonus;

  let grosze = patch.grosze ?? save.grosze;
  let lifetimeGrosze = patch.lifetimeGrosze ?? save.lifetimeGrosze;
  let runLifetimeGrosze = patch.runLifetimeGrosze ?? save.runLifetimeGrosze;
  if (patch.grosze !== undefined && patch.grosze > save.grosze) {
    const gift = patch.grosze - save.grosze;
    runLifetimeGrosze = Math.max(runLifetimeGrosze, save.runLifetimeGrosze + gift);
    lifetimeGrosze = Math.max(lifetimeGrosze, save.lifetimeGrosze + gift);
  }

  return parseBottleClickerSave({
    ...save,
    ...patch,
    grosze,
    lifetimeGrosze,
    runLifetimeGrosze,
    prestigeCount,
    prestigeBonus,
    upgrades: { ...save.upgrades, ...patch.upgrades },
    wipeGeneration: save.wipeGeneration + 1,
    lastSeenMs: now,
  });
}

export function canBuyUpgrade(save: BottleClickerSave, id: BottleClickerUpgradeId): boolean {
  if (!isUpgradeUnlocked(save, id)) return false;
  if (isUpgradeMaxed(save, id)) return false;
  return save.grosze >= upgradeCost(id, save.upgrades[id]);
}

export function buyUpgrade(
  save: BottleClickerSave,
  id: BottleClickerUpgradeId,
  count = 1,
): BottleClickerSave | null {
  const max = upgradeMaxLevel(id);
  const n = clampInt(count, 0, max);
  if (n <= 0) return null;
  if (!isUpgradeUnlocked(save, id)) return null;
  const level = save.upgrades[id];
  if (level + n > max) return null;
  const cost = upgradeCostForCount(id, level, n);
  if (cost <= 0 || save.grosze < cost) return null;
  return {
    ...save,
    grosze: save.grosze - cost,
    upgrades: { ...save.upgrades, [id]: level + n },
  };
}

/** Unlocked businesses, plus the next locked one as a teaser. */
export function visibleUpgrades(save: BottleClickerSave): BottleClickerUpgradeDef[] {
  const visible: BottleClickerUpgradeDef[] = [];
  for (const upgrade of BOTTLE_CLICKER_UPGRADES) {
    if (isUpgradeUnlocked(save, upgrade.id)) {
      visible.push(upgrade);
      continue;
    }
    if (upgrade.hideUntilUnlocked) continue;
    visible.push(upgrade);
    break;
  }
  return visible;
}

export function isCrushedBottle(type: BottleClickerBottle): boolean {
  return type === "crushed";
}

export function isSackBottle(type: BottleClickerBottle): boolean {
  return type === "sack";
}

export function kaucjaFor(type: BottleClickerBottle): number {
  if (type === "crushed" || type === "sack") return 0;
  return BOTTLE_CLICKER_KAUCJA[type];
}

/** Direct click only — splash radius must not count, or piles become mines. */
export function crushedClickReach(width: number, height: number): number {
  return Math.max(width, height) / 2 + BOTTLE_CLICKER.CRUSHED_HIT_PAD;
}

export function canSpawnCrushed(
  returnedBottles: number,
  crushedOnField: number,
  cooldownLeftSec: number,
  maxOnField: number = BOTTLE_CLICKER.CRUSHED_MAX_ON_FIELD,
): boolean {
  return (
    returnedBottles >= BOTTLE_CLICKER.CRUSHED_UNLOCK_RETURNED &&
    crushedOnField < maxOnField &&
    cooldownLeftSec <= 0
  );
}

export function nextCrushedCooldownSec(rng: () => number = Math.random): number {
  const span = BOTTLE_CLICKER.CRUSHED_COOLDOWN_MAX_SEC - BOTTLE_CLICKER.CRUSHED_COOLDOWN_MIN_SEC;
  return BOTTLE_CLICKER.CRUSHED_COOLDOWN_MIN_SEC + clampUnit(rng()) * span;
}

export function canSpawnHunt(
  returnedBottles: number,
  huntOnField: boolean,
  cooldownLeftSec: number,
): boolean {
  return (
    returnedBottles >= BOTTLE_CLICKER.HUNT_UNLOCK_RETURNED &&
    !huntOnField &&
    cooldownLeftSec <= 0
  );
}

export function nextHuntCooldownSec(rng: () => number = Math.random): number {
  const span = BOTTLE_CLICKER.HUNT_COOLDOWN_MAX_SEC - BOTTLE_CLICKER.HUNT_COOLDOWN_MIN_SEC;
  return BOTTLE_CLICKER.HUNT_COOLDOWN_MIN_SEC + clampUnit(rng()) * span;
}

export function clampHuntStreak(streak: number): number {
  return clampInt(streak, 0, BOTTLE_CLICKER.HUNT_STREAK_MAX);
}

export function huntTierById(id: HuntTierId | number): HuntTierDef {
  return HUNT_TIERS.find((tier) => tier.id === id) ?? (HUNT_TIERS[0] as HuntTierDef);
}

export function dealerMenelFloorTier(floor: number): HuntTierDef {
  const index = clampInt(floor, 0, BOTTLE_CLICKER.DEALER_MENEL_FLOOR_MAX);
  return HUNT_TIERS[index] as HuntTierDef;
}

export function huntTierForStreak(streak: number, floor = 0): HuntTierDef {
  const n = Math.max(clampHuntStreak(streak), dealerMenelFloorTier(floor).minStreak);
  let current = HUNT_TIERS[0] as HuntTierDef;
  for (const tier of HUNT_TIERS) {
    if (n >= tier.minStreak) current = tier;
  }
  return current;
}

/** Miss: drop to the previous tier's floor. Dealer karnet will not go below `floor`. */
export function demoteHuntStreak(streak: number, floor = 0): number {
  const current = huntTierForStreak(streak, floor);
  const index = HUNT_TIERS.findIndex((tier) => tier.id === current.id);
  const floorMin = dealerMenelFloorTier(floor).minStreak;
  if (index <= 0) return floorMin;
  return Math.max((HUNT_TIERS[index - 1] as HuntTierDef).minStreak, floorMin);
}

export function huntMaxMult(): number {
  return (HUNT_TIERS[HUNT_TIERS.length - 1] as HuntTierDef).mult;
}

/** Polish decimal for HUD: 2 → "2", 2.5 → "2,5". */
export function formatHuntMult(mult: number): string {
  if (Number.isInteger(mult)) return String(mult);
  return String(mult).replace(".", ",");
}

/** Kaucja multiplier while the recruited menel is working. */
export function huntMultiplier(
  activeUntilMs: number,
  now = Date.now(),
  mult: number = BOTTLE_CLICKER.HUNT_MULT,
): number {
  return now < activeUntilMs ? mult : 1;
}

function spawnDvdBounce(
  now: number,
  rng: () => number,
  size: number,
  speedMin: number,
  speedMax: number,
): DvdBounceState {
  const speed = speedMin + clampUnit(rng()) * (speedMax - speedMin);
  const angle = clampUnit(rng()) * Math.PI * 2;
  let vx = Math.cos(angle) * speed;
  let vy = Math.sin(angle) * speed;
  const minAxis = speed * 0.35;
  if (Math.abs(vx) < minAxis) vx = (vx < 0 ? -1 : 1) * minAxis;
  if (Math.abs(vy) < minAxis) vy = (vy < 0 ? -1 : 1) * minAxis;
  const mag = Math.hypot(vx, vy) || 1;
  vx = (vx / mag) * speed;
  vy = (vy / mag) * speed;

  const maxX = BOTTLE_CLICKER.FIELD_WIDTH - size;
  const maxY = BOTTLE_CLICKER.FIELD_HEIGHT - size;
  const along = clampUnit(rng());
  const edge = Math.floor(clampUnit(rng()) * 4);
  let x = along * maxX;
  let y = along * maxY;
  if (edge === 0) {
    y = 8;
    vy = Math.abs(vy);
  } else if (edge === 1) {
    y = Math.max(8, maxY - 8);
    vy = -Math.abs(vy);
  } else if (edge === 2) {
    x = 8;
    vx = Math.abs(vx);
  } else {
    x = Math.max(8, maxX - 8);
    vx = -Math.abs(vx);
  }
  return { x, y, vx, vy, bornMs: now };
}

function stepDvdBounce<T extends DvdBounceState>(state: T, dt: number, size: number): T {
  let { x, y, vx, vy } = state;
  x += vx * dt;
  y += vy * dt;
  const maxX = BOTTLE_CLICKER.FIELD_WIDTH - size;
  const maxY = BOTTLE_CLICKER.FIELD_HEIGHT - size;
  if (x <= 0) {
    x = 0;
    vx = Math.abs(vx);
  } else if (x >= maxX) {
    x = maxX;
    vx = -Math.abs(vx);
  }
  if (y <= 0) {
    y = 0;
    vy = Math.abs(vy);
  } else if (y >= maxY) {
    y = maxY;
    vy = -Math.abs(vy);
  }
  return { ...state, x, y, vx, vy };
}

function dvdHit(state: DvdBounceState, lx: number, ly: number, size: number, pad: number): boolean {
  const cx = state.x + size / 2;
  const cy = state.y + size / 2;
  return Math.hypot(cx - lx, cy - ly) <= size / 2 + pad;
}

export function huntHit(hunt: HuntState, lx: number, ly: number): boolean {
  const tier = huntTierById(hunt.tier);
  return dvdHit(hunt, lx, ly, tier.size, tier.hitPad);
}

export function spawnHunt(
  now: number,
  rng: () => number = Math.random,
  streak = 0,
  floor = 0,
): HuntState {
  const tier = huntTierForStreak(streak, floor);
  return {
    ...spawnDvdBounce(now, rng, tier.size, tier.speedMin, tier.speedMax),
    tier: tier.id,
  };
}

export function stepHunt(hunt: HuntState, dt: number): HuntState {
  return stepDvdBounce(hunt, dt, huntTierById(hunt.tier).size);
}

export function isHuntAlive(hunt: HuntState, now: number): boolean {
  return now < hunt.bornMs + huntTierById(hunt.tier).lifetimeSec * 1000;
}

export function freezeRemainingMs(
  remainingMs: number,
  savedAtMs: number,
  now: number,
  freeze: boolean,
): number {
  if (freeze) return Math.max(0, remainingMs);
  return Math.max(0, remainingMs - Math.max(0, now - savedAtMs));
}

/** Push field event clocks so a Skup pause does not eat lifetime or cooldown. */
export function shiftSkupEventClocks<
  T extends {
    nextHuntAt: number;
    nextPoliceAt: number;
    nextSackAt: number;
    nextCrushedAt: number;
    nextScooterAt?: number;
    huntBornMs: number | null;
    policeBornMs: number | null;
    scooterBornMs?: number | null;
  },
>(clocks: T, pauseMs: number): T {
  const d = Math.max(0, pauseMs);
  return {
    ...clocks,
    nextHuntAt: clocks.nextHuntAt + d,
    nextPoliceAt: clocks.nextPoliceAt + d,
    nextSackAt: clocks.nextSackAt + d,
    nextCrushedAt: clocks.nextCrushedAt + d,
    ...(clocks.nextScooterAt == null ? {} : { nextScooterAt: clocks.nextScooterAt + d }),
    huntBornMs: clocks.huntBornMs == null ? null : clocks.huntBornMs + d,
    policeBornMs: clocks.policeBornMs == null ? null : clocks.policeBornMs + d,
    ...(clocks.scooterBornMs == null ? {} : { scooterBornMs: clocks.scooterBornMs + d }),
  };
}

export function huntEncounterFromState(
  hunt: HuntState,
  nowPerf: number,
  nowWall = Date.now(),
  paused = false,
): HuntEncounter {
  const lifetime = huntTierById(hunt.tier).lifetimeSec * 1000;
  return {
    remainingMs: Math.max(0, hunt.bornMs + lifetime - nowPerf),
    savedAtMs: nowWall,
    paused,
    tier: hunt.tier,
    x: hunt.x,
    y: hunt.y,
    vx: hunt.vx,
    vy: hunt.vy,
  };
}

export function huntStateFromSave(enc: HuntEncounter, nowPerf: number): HuntState | null {
  if (enc.remainingMs <= 0) return null;
  const lifetime = huntTierById(enc.tier).lifetimeSec * 1000;
  return {
    x: enc.x,
    y: enc.y,
    vx: enc.vx,
    vy: enc.vy,
    tier: enc.tier,
    bornMs: nowPerf - (lifetime - enc.remainingMs),
  };
}

export function policeEncounterFromState(
  police: PoliceState,
  nowPerf: number,
  nowWall = Date.now(),
  paused = false,
): BounceEncounter {
  const lifetime = BOTTLE_CLICKER.POLICE_LIFETIME_SEC * 1000;
  return {
    remainingMs: Math.max(0, police.bornMs + lifetime - nowPerf),
    savedAtMs: nowWall,
    paused,
    x: police.x,
    y: police.y,
    vx: police.vx,
    vy: police.vy,
  };
}

export function policeStateFromSave(enc: BounceEncounter, nowPerf: number): PoliceState | null {
  if (enc.remainingMs <= 0) return null;
  const lifetime = BOTTLE_CLICKER.POLICE_LIFETIME_SEC * 1000;
  return {
    x: enc.x,
    y: enc.y,
    vx: enc.vx,
    vy: enc.vy,
    bornMs: nowPerf - (lifetime - enc.remainingMs),
  };
}

/** Pavement in front of the cabinet: butelkomat bottom → front of the lot. */
export function scooterLotBand(): { backFeet: number; frontFeet: number } {
  const machine = butelkomatRect();
  return {
    backFeet: machine.y + machine.height,
    frontFeet: BOTTLE_CLICKER.FIELD_HEIGHT - BOTTLE_CLICKER.SCOOTER_FLOOR_INSET,
  };
}

export function scooterSpriteYForFeet(feetY: number): number {
  return feetY - BOTTLE_CLICKER.SCOOTER_HEIGHT;
}

/** Front-lot sprite Y — the original FLOOR_Y lane. */
export function scooterFloorY(): number {
  return scooterSpriteYForFeet(scooterLotBand().frontFeet);
}

export function scooterRemainingMs(scooter: ScooterState): number {
  const speed = Math.abs(scooter.vx);
  if (speed <= 0) return 0;
  if (scooter.vx >= 0) {
    return (Math.max(0, BOTTLE_CLICKER.FIELD_WIDTH - scooter.x) / speed) * 1000;
  }
  return (Math.max(0, scooter.x + BOTTLE_CLICKER.SCOOTER_WIDTH) / speed) * 1000;
}

export function isScooterOnField(scooter: ScooterState): boolean {
  if (scooter.vx >= 0) return scooter.x < BOTTLE_CLICKER.FIELD_WIDTH;
  return scooter.x + BOTTLE_CLICKER.SCOOTER_WIDTH > 0;
}

export function scooterHit(scooter: ScooterState, lx: number, ly: number): boolean {
  const cx = scooter.x + BOTTLE_CLICKER.SCOOTER_WIDTH / 2;
  const cy = scooter.y + BOTTLE_CLICKER.SCOOTER_HEIGHT / 2;
  const rx = BOTTLE_CLICKER.SCOOTER_WIDTH / 2 + BOTTLE_CLICKER.SCOOTER_HIT_PAD;
  const ry = BOTTLE_CLICKER.SCOOTER_HEIGHT / 2 + BOTTLE_CLICKER.SCOOTER_HIT_PAD;
  if (rx <= 0 || ry <= 0) return false;
  const dx = (lx - cx) / rx;
  const dy = (ly - cy) / ry;
  return dx * dx + dy * dy <= 1;
}

export function spawnScooter(now: number, rng: () => number = Math.random): ScooterState {
  const span = BOTTLE_CLICKER.SCOOTER_SPEED_MAX - BOTTLE_CLICKER.SCOOTER_SPEED_MIN;
  const speed = BOTTLE_CLICKER.SCOOTER_SPEED_MIN + clampUnit(rng()) * span;
  const goingRight = clampUnit(rng()) < 0.5;
  const { backFeet, frontFeet } = scooterLotBand();
  const feet = backFeet + clampUnit(rng()) * Math.max(0, frontFeet - backFeet);
  return {
    x: goingRight ? -BOTTLE_CLICKER.SCOOTER_WIDTH : BOTTLE_CLICKER.FIELD_WIDTH,
    y: scooterSpriteYForFeet(feet),
    vx: goingRight ? speed : -speed,
    vy: 0,
    bornMs: now,
  };
}

export function stepScooter(scooter: ScooterState, dt: number): ScooterState {
  return { ...scooter, x: scooter.x + scooter.vx * dt, vy: 0 };
}

export function canSpawnScooter(
  returnedBottles: number,
  scooterOnField: boolean,
  cooldownLeftSec: number,
): boolean {
  return (
    returnedBottles >= BOTTLE_CLICKER.SCOOTER_UNLOCK_RETURNED &&
    !scooterOnField &&
    cooldownLeftSec <= 0
  );
}

export function nextScooterCooldownSec(rng: () => number = Math.random): number {
  const span = BOTTLE_CLICKER.SCOOTER_COOLDOWN_MAX_SEC - BOTTLE_CLICKER.SCOOTER_COOLDOWN_MIN_SEC;
  return BOTTLE_CLICKER.SCOOTER_COOLDOWN_MIN_SEC + clampUnit(rng()) * span;
}

/** Kid was hauling PET/cans. Count 8–12, exactly one lucky. */
export function pickScooterBasket(rng: () => number = Math.random): SpawnedBottle[] {
  const span = BOTTLE_CLICKER.SCOOTER_BASKET_MAX - BOTTLE_CLICKER.SCOOTER_BASKET_MIN;
  const count = BOTTLE_CLICKER.SCOOTER_BASKET_MIN + Math.floor(clampUnit(rng()) * (span + 1));
  const luckyIndex = Math.min(count - 1, Math.floor(clampUnit(rng()) * count));
  const out: SpawnedBottle[] = [];
  for (let i = 0; i < count; i += 1) {
    const type: BottleClickerFamily = clampUnit(rng()) < BOTTLE_CLICKER.MIX_CAN_SHARE ? "can" : "pet";
    out.push({
      type,
      art: pickBottleArt(type, rng),
      lucky: i === luckyIndex,
    });
  }
  return out;
}

export function scooterEncounterFromState(
  scooter: ScooterState,
  _nowPerf: number,
  nowWall = Date.now(),
  paused = false,
): BounceEncounter {
  return {
    remainingMs: Math.max(0, Math.round(scooterRemainingMs(scooter))),
    savedAtMs: nowWall,
    paused,
    x: scooter.x,
    y: scooter.y,
    vx: scooter.vx,
    vy: 0,
  };
}

export function scooterStateFromSave(enc: BounceEncounter, nowPerf: number): ScooterState | null {
  if (enc.remainingMs <= 0) return null;
  return {
    x: enc.x,
    y: enc.y,
    vx: enc.vx,
    vy: 0,
    bornMs: nowPerf,
  };
}

export function isFreshAccountSave(save: BottleClickerSave): boolean {
  if (save.wipeGeneration > 0) return false;
  if (save.prestigeCount > 0) return false;
  if (save.lifetimeGrosze > 0) return false;
  if (save.returnedBottles > 0) return false;
  return upgradeSum(save) === 0;
}

/**
 * Guest progress attaches to an empty account. An established cloud run wins
 * over a guest local. Same-user local vs cloud uses preferBottleClickerSave.
 */
export function pickLoginSave(
  local: BottleClickerSave,
  remote: BottleClickerSave | null,
  userId: number,
): BottleClickerSave {
  const stamp = (save: BottleClickerSave): BottleClickerSave => ({ ...save, ownerUserId: userId });
  const owner = local.ownerUserId;
  if (owner != null && owner !== userId) {
    return stamp(remote ?? emptyBottleClickerSave());
  }
  if (!remote || isFreshAccountSave(remote)) return stamp(local);
  if (owner === userId) return stamp(preferBottleClickerSave(local, remote));
  return stamp(remote);
}

export function applyBottleClickerBootSession(
  save: BottleClickerSave,
  now = Date.now(),
  freezeEvents = false,
): { save: BottleClickerSave; huntMissed: boolean } {
  let next: BottleClickerSave = { ...save };
  let huntMissed = false;

  if (next.huntEncounter) {
    const left = freezeRemainingMs(
      next.huntEncounter.remainingMs,
      next.huntEncounter.savedAtMs,
      now,
      freezeEvents || Boolean(next.huntEncounter.paused),
    );
    if (left <= 0) {
      next = {
        ...next,
        huntStreak: demoteHuntStreak(next.huntStreak, next.dealerMenelFloor),
        huntEncounter: null,
      };
      huntMissed = true;
    } else {
      next = {
        ...next,
        huntEncounter: {
          ...next.huntEncounter,
          remainingMs: left,
          savedAtMs: now,
          paused: freezeEvents,
        },
      };
    }
  }

  if (next.policeEncounter) {
    const left = freezeRemainingMs(
      next.policeEncounter.remainingMs,
      next.policeEncounter.savedAtMs,
      now,
      freezeEvents || Boolean(next.policeEncounter.paused),
    );
    if (left <= 0) {
      next = { ...next, policeEncounter: null };
    } else {
      next = {
        ...next,
        policeEncounter: {
          ...next.policeEncounter,
          remainingMs: left,
          savedAtMs: now,
          paused: freezeEvents,
        },
      };
    }
  }

  if (next.scooterEncounter) {
    const left = freezeRemainingMs(
      next.scooterEncounter.remainingMs,
      next.scooterEncounter.savedAtMs,
      now,
      freezeEvents || Boolean(next.scooterEncounter.paused),
    );
    if (left <= 0) {
      next = { ...next, scooterEncounter: null };
    } else {
      next = {
        ...next,
        scooterEncounter: {
          ...next.scooterEncounter,
          remainingMs: left,
          savedAtMs: now,
          paused: freezeEvents,
        },
      };
    }
  }

  if (!freezeEvents) {
    const seen = next.lastSeenMs > 0 && next.lastSeenMs <= now ? next.lastSeenMs : now;
    const elapsed = Math.max(0, now - seen);
    next = {
      ...next,
      nextHuntRemainingMs: Math.max(0, next.nextHuntRemainingMs - elapsed),
      nextPoliceRemainingMs: Math.max(0, next.nextPoliceRemainingMs - elapsed),
      nextSackRemainingMs: Math.max(0, next.nextSackRemainingMs - elapsed),
      nextScooterRemainingMs: Math.max(0, next.nextScooterRemainingMs - elapsed),
    };
    if (next.huntBuffUntilMs > 0 && next.huntBuffUntilMs <= now) {
      next = { ...next, huntBuffUntilMs: 0, huntBuffTier: 0 };
    }
    if (next.policeBuffUntilMs > 0 && next.policeBuffUntilMs <= now) {
      next = { ...next, policeBuffUntilMs: 0 };
    }
    if (next.sackBuffUntilMs > 0 && next.sackBuffUntilMs <= now) {
      next = { ...next, sackBuffUntilMs: 0 };
    }
  }

  return { save: next, huntMissed };
}

export function canSpawnPolice(
  returnedBottles: number,
  policeOnField: boolean,
  cooldownLeftSec: number,
): boolean {
  return (
    returnedBottles >= BOTTLE_CLICKER.POLICE_UNLOCK_RETURNED &&
    !policeOnField &&
    cooldownLeftSec <= 0
  );
}

export function nextPoliceCooldownSec(rng: () => number = Math.random): number {
  const span = BOTTLE_CLICKER.POLICE_COOLDOWN_MAX_SEC - BOTTLE_CLICKER.POLICE_COOLDOWN_MIN_SEC;
  return BOTTLE_CLICKER.POLICE_COOLDOWN_MIN_SEC + clampUnit(rng()) * span;
}

export function policePayoutMultiplier(activeUntilMs: number, now = Date.now()): number {
  return now < activeUntilMs ? BOTTLE_CLICKER.POLICE_PAYOUT_MULT : 1;
}

export function policeCrushedMultiplier(activeUntilMs: number, now = Date.now()): number {
  return now < activeUntilMs ? BOTTLE_CLICKER.POLICE_CRUSHED_MULT : 1;
}

export function policeHit(police: PoliceState, lx: number, ly: number): boolean {
  return dvdHit(police, lx, ly, BOTTLE_CLICKER.POLICE_SIZE, BOTTLE_CLICKER.POLICE_HIT_PAD);
}

export function spawnPolice(now: number, rng: () => number = Math.random): PoliceState {
  return spawnDvdBounce(
    now,
    rng,
    BOTTLE_CLICKER.POLICE_SIZE,
    BOTTLE_CLICKER.POLICE_SPEED_MIN,
    BOTTLE_CLICKER.POLICE_SPEED_MAX,
  );
}

export function stepPolice(police: PoliceState, dt: number): PoliceState {
  return stepDvdBounce(police, dt, BOTTLE_CLICKER.POLICE_SIZE);
}

export function isPoliceAlive(police: PoliceState, now: number): boolean {
  return now < police.bornMs + BOTTLE_CLICKER.POLICE_LIFETIME_SEC * 1000;
}

export function crushedMaxOnField(spawnMult = 1): number {
  return Math.max(
    BOTTLE_CLICKER.CRUSHED_MAX_ON_FIELD,
    Math.round(BOTTLE_CLICKER.CRUSHED_MAX_ON_FIELD * Math.max(1, spawnMult)),
  );
}

export function canSpawnSack(
  returnedBottles: number,
  sackOnField: number,
  cooldownLeftSec: number,
): boolean {
  return (
    returnedBottles >= BOTTLE_CLICKER.SACK_UNLOCK_RETURNED &&
    sackOnField < BOTTLE_CLICKER.SACK_MAX_ON_FIELD &&
    cooldownLeftSec <= 0
  );
}

export function nextSackCooldownSec(rng: () => number = Math.random): number {
  const span = BOTTLE_CLICKER.SACK_COOLDOWN_MAX_SEC - BOTTLE_CLICKER.SACK_COOLDOWN_MIN_SEC;
  return BOTTLE_CLICKER.SACK_COOLDOWN_MIN_SEC + clampUnit(rng()) * span;
}

/** Rain density for a catch. Level 0 is still a rain — shop copies only thicken it. */
export function sackRainMultiplier(sackLevel: number): number {
  const n = clampInt(sackLevel, 0, upgradeMaxLevel("sack"));
  return Math.min(
    BOTTLE_CLICKER.SACK_MAX_MULT,
    BOTTLE_CLICKER.SACK_MULT_BASE + n * BOTTLE_CLICKER.SACK_MULT_PER_LEVEL,
  );
}

export function sackSpawnMultiplier(
  sackLevel: number,
  activeUntilMs: number,
  now = Date.now(),
): number {
  return now < activeUntilMs ? sackRainMultiplier(sackLevel) : 1;
}

/** Direct click only — splash and zbieracze never count. */
export function sackClickReach(width: number, height: number): number {
  return Math.max(width, height) / 2 + BOTTLE_CLICKER.SACK_HIT_PAD;
}

/**
 * A tenth of the wallet, or a handful of typical bottles if that is more.
 * Nothing under 1 zł, and never more than 25% so an early misclick is not a wipe.
 */
export function crushedPenaltyGrosze(save: BottleClickerSave): number {
  if (save.grosze < BOTTLE_CLICKER.CRUSHED_GRACE_GROSZE) return 0;
  const typical = Math.max(
    1,
    Math.round(
      averageKaucja(save.upgrades.mix) *
        firmaMultiplier(save.upgrades.firma) *
        prestigeMultiplier(save.prestigeBonus),
    ),
  );
  const minSlap = typical * BOTTLE_CLICKER.CRUSHED_MIN_BOTTLES;
  const share = Math.max(1, Math.floor(save.grosze * BOTTLE_CLICKER.CRUSHED_WALLET_SHARE));
  const cap = Math.max(1, Math.floor(save.grosze * BOTTLE_CLICKER.CRUSHED_WALLET_CAP));
  return Math.min(save.grosze, cap, Math.max(minSlap, share));
}

export function comboMultiplier(combo: number): number {
  const steps = clampInt(combo, 1, BOTTLE_CLICKER.COMBO_CAP + 1) - 1;
  return 1 + steps * BOTTLE_CLICKER.COMBO_STEP;
}

export function firmaMultiplier(firmaLevel: number): number {
  return 1 + clampInt(firmaLevel, 0, upgradeMaxLevel("firma")) * BOTTLE_CLICKER.FIRMA_BONUS;
}

export function clickMultiplier(clickLevel: number, manual: boolean): number {
  if (!manual) return 1;
  return 1 + clampInt(clickLevel, 0, upgradeMaxLevel("click")) * BOTTLE_CLICKER.CLICK_BONUS;
}

export function payoutForBottle(
  save: BottleClickerSave,
  type: BottleClickerBottle,
  ctx: BottleReturnContext = DEFAULT_RETURN,
): number {
  if (type === "crushed") {
    const penalty = crushedPenaltyGrosze(save);
    return penalty > 0 ? -penalty : 0;
  }
  if (type === "sack") return 0;
  let pay = kaucjaFor(type);
  pay *= firmaMultiplier(save.upgrades.firma);
  pay *= prestigeMultiplier(save.prestigeBonus);
  pay *= clickMultiplier(save.upgrades.click, ctx.manual);
  pay *= comboMultiplier(ctx.combo);
  if (ctx.lucky) pay *= BOTTLE_CLICKER.LUCKY_MULT;
  if (ctx.hunt) pay *= ctx.huntMult ?? BOTTLE_CLICKER.HUNT_MULT;
  if (ctx.police) pay *= BOTTLE_CLICKER.POLICE_PAYOUT_MULT;
  return Math.max(1, Math.round(pay));
}

export function creditReturn(
  save: BottleClickerSave,
  type: BottleClickerBottle,
  ctx: BottleReturnContext = DEFAULT_RETURN,
): BottleClickerSave {
  if (type === "crushed") {
    const penalty = crushedPenaltyGrosze(save);
    return { ...save, grosze: clampGrosze(save.grosze - penalty) };
  }
  if (type === "sack") return save;
  const pay = payoutForBottle(save, type, ctx);
  const combo = clampInt(ctx.combo, 0, 10_000);
  return {
    ...save,
    grosze: clampGrosze(save.grosze + pay),
    lifetimeGrosze: clampGrosze(save.lifetimeGrosze + pay),
    runLifetimeGrosze: clampGrosze(save.runLifetimeGrosze + pay),
    returnedBottles: save.returnedBottles + 1,
    bestCombo: Math.max(save.bestCombo, combo),
  };
}

/**
 * Mix starts as PET only. Cans unlock at level 1, glass at 3, then the table
 * leans toward the 1 zł bottles.
 */
export function bottleWeights(mixLevel: number): BottleWeights {
  const level = clampInt(mixLevel, 0, upgradeMaxLevel("mix"));
  if (level <= 0) return { pet: 1000, can: 0, glass: 0 };
  if (level === 1) return { pet: 700, can: 300, glass: 0 };
  if (level === 2) return { pet: 500, can: 500, glass: 0 };

  const glass = Math.min(
    BOTTLE_CLICKER.MIX_GLASS_START_WEIGHT +
      (level - BOTTLE_CLICKER.MIX_GLASS_FROM_LEVEL) * BOTTLE_CLICKER.MIX_GLASS_WEIGHT_PER_LEVEL,
    BOTTLE_CLICKER.MIX_GLASS_MAX_WEIGHT,
  );
  const rest = 1000 - glass;
  const can = Math.round(rest * BOTTLE_CLICKER.MIX_CAN_SHARE);
  return { pet: rest - can, can, glass };
}

export function luckyChance(mixLevel: number): number {
  const mix = clampInt(mixLevel, 0, upgradeMaxLevel("mix"));
  return Math.min(
    BOTTLE_CLICKER.MAX_LUCKY_CHANCE,
    BOTTLE_CLICKER.BASE_LUCKY_CHANCE + mix * BOTTLE_CLICKER.MIX_LUCKY_CHANCE,
  );
}

export function pickSpawnedBottle(
  mixLevel: number,
  rng: () => number = Math.random,
): SpawnedBottle {
  const type = pickBottleType(mixLevel, rng);
  return {
    type,
    art: pickBottleArt(type, rng),
    lucky: clampUnit(rng()) < luckyChance(mixLevel),
  };
}

export function pickBottleType(
  mixLevel: number,
  rng: () => number = Math.random,
): BottleClickerFamily {
  const weights = bottleWeights(mixLevel);
  const total = weights.pet + weights.can + weights.glass;
  const roll = clampUnit(rng()) * total;
  if (roll < weights.pet) return "pet";
  if (roll < weights.pet + weights.can) return "can";
  return "glass";
}

/** Even split across the three labeled skins for a deposit family. */
export function pickBottleArt(
  family: BottleClickerFamily,
  rng: () => number = Math.random,
): BottleClickerArtId {
  const list = BOTTLE_CLICKER_VARIANTS[family];
  const index = Math.min(list.length - 1, Math.floor(clampUnit(rng()) * list.length));
  return list[index] ?? family;
}

/** Each sklep adds bottles on the field. Additive, like Cookie Clicker buildings. */
export function spawnPerSec(spawnLevel: number): number {
  const level = clampInt(spawnLevel, 0, upgradeMaxLevel("spawn"));
  return Math.min(
    BOTTLE_CLICKER.MAX_SPAWN_PER_SEC,
    BOTTLE_CLICKER.BASE_SPAWN_PER_SEC + level * BOTTLE_CLICKER.SPAWN_PER_LEVEL,
  );
}

export function autoPerSec(autoLevel: number): number {
  const level = clampInt(autoLevel, 0, upgradeMaxLevel("auto"));
  return Math.min(BOTTLE_CLICKER.MAX_AUTO_PER_SEC, level * BOTTLE_CLICKER.AUTO_PER_LEVEL);
}

export function spawnIntervalMs(spawnLevel: number): number {
  return Math.max(1, Math.round(1000 / spawnPerSec(spawnLevel)));
}

export function autoIntervalMs(autoLevel: number): number | null {
  const rate = autoPerSec(autoLevel);
  if (rate <= 0) return null;
  return Math.max(1, Math.round(1000 / rate));
}

export function splashRadiusPx(grabLevel: number, clickLevel: number): number {
  return Math.min(
    MAX_SPLASH_RADIUS_PX,
    BOTTLE_CLICKER.BASE_CLICK_PAD +
      clampInt(grabLevel, 0, upgradeMaxLevel("grab")) * BOTTLE_CLICKER.CLICK_PAD_PER_GRAB +
      clampInt(clickLevel, 0, upgradeMaxLevel("click")) * BOTTLE_CLICKER.CLICK_PAD_PER_CLICK,
  );
}

export function splashCount(clickLevel: number): number {
  const level = clampInt(clickLevel, 0, upgradeMaxLevel("click"));
  return Math.min(BOTTLE_CLICKER.MAX_BOTTLES, 1 + Math.floor(level / 2));
}

/** @deprecated use splashRadiusPx — kept so older tests/call sites still compile. */
export function clickPaddingPx(grabLevel: number): number {
  return splashRadiusPx(grabLevel, 0);
}

export function averageKaucja(mixLevel: number): number {
  const weights = bottleWeights(mixLevel);
  const total = weights.pet + weights.can + weights.glass;
  if (total <= 0) return BOTTLE_CLICKER_KAUCJA.pet;
  return (
    (weights.pet * BOTTLE_CLICKER_KAUCJA.pet +
      weights.can * BOTTLE_CLICKER_KAUCJA.can +
      weights.glass * BOTTLE_CLICKER_KAUCJA.glass) /
    total
  );
}

/** Idle zł/s if collectors keep up with spawn. Clicks are extra. */
export function estimatedAutoGroszePerSec(save: BottleClickerSave, spawnMult = 1): number {
  const spawn = spawnPerSec(save.upgrades.spawn) * Math.max(1, spawnMult);
  const caught = Math.min(spawn, autoPerSec(save.upgrades.auto));
  return (
    caught *
    averageKaucja(save.upgrades.mix) *
    firmaMultiplier(save.upgrades.firma) *
    prestigeMultiplier(save.prestigeBonus)
  );
}

/**
 * Highest level a budget could have paid for on its own. Used to keep a forged
 * save from claiming Operator 200 and inflating the plausibility ceiling with it.
 */
export function affordableLevel(id: BottleClickerUpgradeId, budgetGrosze: number): number {
  if (budgetGrosze <= 0) return 0;
  const base = bottleClickerUpgrade(id).baseCost;
  const growth = BOTTLE_CLICKER.COST_GROWTH;
  const levels = Math.log(1 + (budgetGrosze * (growth - 1)) / base) / Math.log(growth);
  // One spare level: a rebalance can make copies bought at old prices look unaffordable.
  return clampInt(Math.floor(levels) + 1, 0, upgradeMaxLevel(id));
}

/**
 * The most kaucja per second a save could honestly be earning: every bottle
 * golden, combo pinned at its cap, and taps at the sustained human ceiling.
 * Deliberately generous — it exists to catch orders of magnitude, not skill.
 */
export function maxPlausibleGroszePerSec(save: BottleClickerSave): number {
  const level = (id: BottleClickerUpgradeId) =>
    Math.min(save.upgrades[id], affordableLevel(id, save.lifetimeGrosze));
  const spawn =
    spawnPerSec(level("spawn")) *
    (save.prestigeCount >= BOTTLE_CLICKER.SACK_UNLOCK_PRESTIGE
      ? sackRainMultiplier(level("sack"))
      : 1);
  const perBottle =
    averageKaucja(level("mix")) *
    firmaMultiplier(level("firma")) *
    prestigeMultiplier(save.prestigeBonus) *
    BOTTLE_CLICKER.LUCKY_MULT *
    huntMaxMult();
  const tapped = Math.min(
    spawn,
    BOTTLE_CLICKER_INPUT.SUSTAINED_TAPS_PER_SEC * splashCount(level("click")),
  );
  const collected = Math.min(autoPerSec(level("auto")), Math.max(0, spawn - tapped));
  const manual =
    tapped *
    clickMultiplier(level("click"), true) *
    comboMultiplier(BOTTLE_CLICKER.COMBO_CAP + 1);
  return (manual + collected) * perBottle;
}

/** Clock skew, a splash landing right on the boundary, and one offline credit. */
const PLAUSIBILITY_GRACE_SEC = 120;

/**
 * Could `incoming` really have been earned from `stored` in `elapsedMs`?
 * Lifetime kaucja is the yardstick because prestige never resets it.
 */
export function isBottleClickerGainPlausible(
  stored: BottleClickerSave,
  incoming: BottleClickerSave,
  elapsedMs: number,
): boolean {
  const gained = incoming.lifetimeGrosze - stored.lifetimeGrosze;
  if (gained <= 0) return true;
  const seconds = Math.max(0, elapsedMs) / 1000 + PLAUSIBILITY_GRACE_SEC;
  return gained <= maxPlausibleGroszePerSec(incoming) * seconds;
}

/** How much idle kaucja is waiting, without touching the wallet. */
export function offlineWindow(
  save: BottleClickerSave,
  now = Date.now(),
): { earned: number; ms: number } {
  const last = save.lastSeenMs;
  if (last <= 0 || now <= last) return { earned: 0, ms: 0 };
  const away = now - last;
  const capped = Math.min(offlineCapMs(save), away);
  if (capped < BOTTLE_CLICKER.OFFLINE_MIN_MS) return { earned: 0, ms: capped };
  const token = clampInt(save.dealerSkupBonusMs, 0, BOTTLE_CLICKER.DEALER_SKUP_TOKEN_MS);
  const ms = capped + token;
  const earned = Math.floor((estimatedAutoGroszePerSec(save) * ms) / 1000);
  return { earned: Math.max(0, earned), ms };
}

/** Add a resolved Skup (or any offline) payout and stamp lastSeen. */
export function creditOfflineGrosze(
  save: BottleClickerSave,
  grosze: number,
  now = Date.now(),
): BottleClickerSave {
  const earned = Math.max(0, Math.floor(grosze));
  return {
    ...save,
    grosze: clampGrosze(save.grosze + earned),
    lifetimeGrosze: clampGrosze(save.lifetimeGrosze + earned),
    runLifetimeGrosze: clampGrosze(save.runLifetimeGrosze + earned),
    lastSeenMs: now,
  };
}

/**
 * Credit idle kaucja for time away. Missing lastSeen does not pay out — that
 * would dump 8 hours onto every v1 save the first time they load v2.
 */
export function applyOfflineEarnings(
  save: BottleClickerSave,
  now = Date.now(),
): { save: BottleClickerSave; earned: number; ms: number } {
  const { earned, ms } = offlineWindow(save, now);
  if (earned <= 0) {
    return { save: { ...save, lastSeenMs: now }, earned: 0, ms };
  }
  return { save: creditOfflineGrosze(save, earned, now), earned, ms };
}

export function butelkomatRect(): { x: number; y: number; width: number; height: number } {
  return {
    x: (BOTTLE_CLICKER.FIELD_WIDTH - BOTTLE_CLICKER.MACHINE_WIDTH) / 2,
    y: (BOTTLE_CLICKER.FIELD_HEIGHT - BOTTLE_CLICKER.MACHINE_HEIGHT) / 2,
    width: BOTTLE_CLICKER.MACHINE_WIDTH,
    height: BOTTLE_CLICKER.MACHINE_HEIGHT,
  };
}

/** Dark circle on the machine face — bottles fly into this hole. */
export function butelkomatMouth(): { x: number; y: number } {
  const machine = butelkomatRect();
  return {
    x: machine.x + BOTTLE_CLICKER.MACHINE_MOUTH_X,
    y: machine.y + BOTTLE_CLICKER.MACHINE_MOUTH_Y,
  };
}

/** LCD panel showing kaucja, aligned to the cutout in the sprite. */
export function butelkomatScreen(): {
  x: number;
  y: number;
  width: number;
  height: number;
} {
  const machine = butelkomatRect();
  return {
    x: machine.x + BOTTLE_CLICKER.MACHINE_SCREEN_X,
    y: machine.y + BOTTLE_CLICKER.MACHINE_SCREEN_Y,
    width: BOTTLE_CLICKER.MACHINE_SCREEN_W,
    height: BOTTLE_CLICKER.MACHINE_SCREEN_H,
  };
}

/** Beside the butelkomat on the lot, not through the cabinet. */
export function dealerRect(): { x: number; y: number; width: number; height: number } {
  const machine = butelkomatRect();
  const width = BOTTLE_CLICKER.DEALER_WIDTH;
  const height = BOTTLE_CLICKER.DEALER_HEIGHT;
  return {
    x: machine.x + machine.width + 18,
    y: machine.y + machine.height - height,
    width,
    height,
  };
}

export function dealerHit(lx: number, ly: number): boolean {
  const box = dealerRect();
  const pad = BOTTLE_CLICKER.DEALER_HIT_PAD;
  return (
    lx >= box.x - pad &&
    lx <= box.x + box.width + pad &&
    ly >= box.y - pad &&
    ly <= box.y + box.height + pad
  );
}

function formatPlainZlote(grosze: number): string {
  const negative = grosze < 0;
  const abs = Math.abs(Math.trunc(grosze));
  const zlote = Math.floor(abs / 100);
  const gr = abs % 100;
  const body = `${zlote.toLocaleString("pl-PL")},${String(gr).padStart(2, "0")} zł`;
  return negative ? `-${body}` : body;
}

function formatCompact(zlote: number, suffix: string): string {
  const rounded = zlote >= 100 ? zlote.toFixed(0) : zlote.toFixed(2);
  const body = `${rounded.replace(".", ",").replace(/,00$/, "")} ${suffix}`;
  return body;
}

/** `12,50 zł`, then `1,25 mln zł` once the empire gets silly. */
export function formatKaucja(grosze: number): string {
  const negative = grosze < 0;
  const abs = Math.abs(Math.trunc(grosze));
  const zlote = abs / 100;
  let body: string;
  if (zlote >= 1_000_000_000) body = formatCompact(zlote / 1_000_000_000, "mld zł");
  else if (zlote >= 1_000_000) body = formatCompact(zlote / 1_000_000, "mln zł");
  else if (zlote >= 10_000) body = `${Math.round(zlote).toLocaleString("pl-PL")} zł`;
  else body = formatPlainZlote(abs);
  return negative ? `-${body}` : body;
}

export function formatKaucjaDelta(grosze: number): string {
  if (grosze > 0) return `+${formatKaucja(grosze)}`;
  if (grosze < 0) return `-${formatKaucja(-grosze)}`;
  return formatKaucja(0);
}

export function formatKaucjaPerSec(groszePerSec: number): string {
  if (groszePerSec <= 0) return "0,00 zł/s";
  return `${formatKaucja(Math.round(groszePerSec))}/s`;
}

/** Remaining hunt buff as `m:ss`. */
export function formatHuntClock(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

export function parseBottleClickerSave(raw: unknown): BottleClickerSave {
  const empty = emptyBottleClickerSave();
  if (typeof raw !== "object" || raw === null) return empty;
  const data = raw as Record<string, unknown>;
  const upgradesRaw =
    typeof data.upgrades === "object" && data.upgrades !== null
      ? (data.upgrades as Record<string, unknown>)
      : {};

  const upgrades = { ...EMPTY_UPGRADES };
  for (const id of UPGRADE_IDS) {
    upgrades[id] = clampInt(asNumber(upgradesRaw[id]), 0, upgradeMaxLevel(id));
  }

  const lifetimeGrosze = clampGrosze(asNumber(data.lifetimeGrosze));
  const runLifetimeGrosze =
    data.runLifetimeGrosze === undefined
      ? lifetimeGrosze
      : clampGrosze(asNumber(data.runLifetimeGrosze));
  const prestigeCount = clampInt(asNumber(data.prestigeCount), 0, 999);
  // Pre-√zł saves only stored the restart count — migrate as ASAP flat +25% each.
  const prestigeBonus =
    data.prestigeBonus === undefined
      ? clampPrestigeBonus(prestigeCount * BOTTLE_CLICKER.PRESTIGE_BONUS)
      : clampPrestigeBonus(asNumber(data.prestigeBonus));

  return {
    version: BOTTLE_CLICKER.SAVE_VERSION,
    grosze: clampGrosze(asNumber(data.grosze)),
    lifetimeGrosze,
    runLifetimeGrosze,
    returnedBottles: clampInt(asNumber(data.returnedBottles), 0, BOTTLE_CLICKER.MAX_GROSZE),
    bestCombo: clampInt(asNumber(data.bestCombo), 0, 10_000),
    prestigeCount,
    prestigeBonus,
    coupons: parseCoupons(data.coupons),
    wipeGeneration: clampInt(asNumber(data.wipeGeneration), 0, 9999),
    lastSeenMs: clampInt(asNumber(data.lastSeenMs), 0, 8_640_000_000_000_000),
    upgrades,
    huntStreak: clampHuntStreak(asNumber(data.huntStreak)),
    ownerUserId: parseOwnerUserId(data.ownerUserId),
    huntEncounter: parseHuntEncounter(data.huntEncounter),
    huntBuffUntilMs: clampInt(asNumber(data.huntBuffUntilMs), 0, 8_640_000_000_000_000),
    huntBuffTier: parseHuntBuffTier(data.huntBuffTier),
    nextHuntRemainingMs: clampInt(asNumber(data.nextHuntRemainingMs), 0, 86_400_000),
    policeEncounter: parseBounceEncounter(data.policeEncounter),
    policeBuffUntilMs: clampInt(asNumber(data.policeBuffUntilMs), 0, 8_640_000_000_000_000),
    nextPoliceRemainingMs: clampInt(asNumber(data.nextPoliceRemainingMs), 0, 86_400_000),
    sackBuffUntilMs: clampInt(asNumber(data.sackBuffUntilMs), 0, 8_640_000_000_000_000),
    nextSackRemainingMs: clampInt(asNumber(data.nextSackRemainingMs), 0, 86_400_000),
    scooterEncounter: parseBounceEncounter(data.scooterEncounter),
    nextScooterRemainingMs: clampInt(asNumber(data.nextScooterRemainingMs), 0, 86_400_000),
    dealerMenelFloor: clampInt(asNumber(data.dealerMenelFloor), 0, BOTTLE_CLICKER.DEALER_MENEL_FLOOR_MAX),
    dealerOfflineBonusMs: clampInt(
      asNumber(data.dealerOfflineBonusMs),
      0,
      BOTTLE_CLICKER.DEALER_OFFLINE_BONUS_MAX_MS,
    ),
    dealerMachineSkin: clampInt(asNumber(data.dealerMachineSkin), 0, 1),
    dealerSkupBonusMs: clampInt(asNumber(data.dealerSkupBonusMs), 0, BOTTLE_CLICKER.DEALER_SKUP_TOKEN_MS),
  };
}

function parseOwnerUserId(raw: unknown): number | null {
  if (raw == null) return null;
  const id = clampInt(asNumber(raw), 0, 1_000_000_000);
  return id > 0 ? id : null;
}

function parseHuntBuffTier(raw: unknown): HuntTierId | 0 {
  const id = clampInt(asNumber(raw), 0, 4);
  return id === 1 || id === 2 || id === 3 || id === 4 ? id : 0;
}

function parseBounceEncounter(raw: unknown): BounceEncounter | null {
  if (typeof raw !== "object" || raw === null) return null;
  const data = raw as Record<string, unknown>;
  const remainingMs = clampInt(asNumber(data.remainingMs), 0, 120_000);
  if (remainingMs <= 0) return null;
  return {
    remainingMs,
    savedAtMs: clampInt(asNumber(data.savedAtMs), 0, 8_640_000_000_000_000),
    paused: data.paused === true,
    x: clampInt(asNumber(data.x), -400, BOTTLE_CLICKER.FIELD_WIDTH + 400),
    y: clampInt(asNumber(data.y), -400, BOTTLE_CLICKER.FIELD_HEIGHT + 400),
    vx: asNumber(data.vx),
    vy: asNumber(data.vy),
  };
}

function parseHuntEncounter(raw: unknown): HuntEncounter | null {
  const bounce = parseBounceEncounter(raw);
  if (!bounce) return null;
  const data = raw as Record<string, unknown>;
  const tier = parseHuntBuffTier(data.tier);
  return { ...bounce, tier: tier === 0 ? 1 : tier };
}

function parseCoupons(raw: unknown): BottleClickerCoupon[] {
  if (!Array.isArray(raw)) return [];
  const out: BottleClickerCoupon[] = [];
  for (const item of raw) {
    if (typeof item !== "object" || item === null) continue;
    const data = item as Record<string, unknown>;
    const linesRaw = Array.isArray(data.lines) ? data.lines : [];
    const lines: { label: string; count: number; grosze: number }[] = [];
    for (const line of linesRaw) {
      if (typeof line !== "object" || line === null) continue;
      const row = line as Record<string, unknown>;
      const label = typeof row.label === "string" ? row.label.slice(0, 40) : "";
      if (!label) continue;
      lines.push({
        label,
        count: clampInt(asNumber(row.count), 0, BOTTLE_CLICKER.MAX_GROSZE),
        grosze: clampGrosze(asNumber(row.grosze)),
      });
    }
    out.push({
      runGrosze: clampGrosze(asNumber(data.runGrosze)),
      returnedBottles: clampInt(asNumber(data.returnedBottles), 0, BOTTLE_CLICKER.MAX_GROSZE),
      lines,
      nextMult: Math.min(11, Math.max(1, asNumber(data.nextMult) || 1)),
      bonusPct: clampInt(asNumber(data.bonusPct), 0, 1_000),
      chipsGranted: clampInt(asNumber(data.chipsGranted), 0, BOTTLE_CLICKER.MAX_GROSZE),
      prestigeCountAfter: clampInt(asNumber(data.prestigeCountAfter), 0, 999),
      printedAtMs: clampInt(asNumber(data.printedAtMs), 0, 8_640_000_000_000_000),
      barcode: typeof data.barcode === "string" ? data.barcode.replace(/\D/g, "").slice(0, 32) : "",
      txnId: typeof data.txnId === "string" ? data.txnId.slice(0, 40) : "",
    });
    if (out.length >= BOTTLE_CLICKER.COUPON_PARSE_MAX) break;
  }
  return out;
}

function upgradeSum(save: BottleClickerSave): number {
  let total = 0;
  for (const id of UPGRADE_IDS) total += save.upgrades[id];
  return total;
}

/**
 * True when `a` is a strictly further-along empire than `b`.
 * Wallet size is ignored so shop spends and crushed-bottle slaps still save.
 */
export function isBottleClickerSaveAhead(a: BottleClickerSave, b: BottleClickerSave): boolean {
  if (a.wipeGeneration !== b.wipeGeneration) return a.wipeGeneration > b.wipeGeneration;
  if (a.prestigeCount !== b.prestigeCount) return a.prestigeCount > b.prestigeCount;
  if (a.lifetimeGrosze !== b.lifetimeGrosze) return a.lifetimeGrosze > b.lifetimeGrosze;
  if (a.returnedBottles !== b.returnedBottles) return a.returnedBottles > b.returnedBottles;
  const upgradesA = upgradeSum(a);
  const upgradesB = upgradeSum(b);
  if (upgradesA !== upgradesB) return upgradesA > upgradesB;
  if (a.dealerMenelFloor !== b.dealerMenelFloor) return a.dealerMenelFloor > b.dealerMenelFloor;
  if (a.dealerOfflineBonusMs !== b.dealerOfflineBonusMs) {
    return a.dealerOfflineBonusMs > b.dealerOfflineBonusMs;
  }
  if (a.dealerMachineSkin !== b.dealerMachineSkin) return a.dealerMachineSkin > b.dealerMachineSkin;
  return false;
}

/**
 * Keep the further-along run when a browser save and a cloud save disagree.
 * Wallet is the last tie-break; a remaining tie keeps `preferredOnTie`.
 */
export function preferBottleClickerSave(
  preferredOnTie: BottleClickerSave,
  other: BottleClickerSave,
): BottleClickerSave {
  if (isBottleClickerSaveAhead(other, preferredOnTie)) return other;
  if (isBottleClickerSaveAhead(preferredOnTie, other)) return preferredOnTie;
  if (other.grosze > preferredOnTie.grosze) return other;
  return preferredOnTie;
}

function asNumber(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function clampInt(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, Math.floor(value)));
}

function clampGrosze(value: number): number {
  return clampInt(value, 0, BOTTLE_CLICKER.MAX_GROSZE);
}

function clampPrestigeBonus(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(maxPrestigeBonus(), Math.max(0, value));
}

function clampUnit(value: number): number {
  if (!Number.isFinite(value)) return 0;
  if (value <= 0) return 0;
  if (value >= 1) return 0.999999;
  return value;
}
